import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiClient } from "../../../api/apiClient";
import flowerIcon from "../../../assets/img/flower_icon.png";
import "../../../assets/css/admin/organization/OrgBoardForm.css";
import "../../../assets/css/admin/AdminCommon.css";

const unitList = [
  { key: "scitech", name: "과학기술대학", collegeId: 1 },
  { key: "global", name: "글로벌융합대학", collegeId: 2 },
  { key: "future", name: "미래인재대학", collegeId: 3 },
  { key: "pharm", name: "약학대학", collegeId: 4 },
  { key: "artdesign", name: "아트앤디자인대학", collegeId: 5 },
  { key: "club", name: "동아리연합회", collegeId: 6 },
  { key: "audit", name: "감사위원회", collegeId: 7 },
];

const categoryOptions = [
  { label: "회계", value: "BUDGET" },
  { label: "행사/사업", value: "EVENT" },
  { label: "행정/운영", value: "ADMINISTRATION" },
  { label: "기타", value: "ETC" },
];

function Label({ children }) {
  return (
    <div className="org-board-form-label">
      <img src={flowerIcon} alt="" />
      <span>{children}</span>
    </div>
  );
}

const getStoredCollegeId = () => {
  const value = localStorage.getItem("collegeId");

  if (!value || value === "null" || value === "undefined") {
    return null;
  }

  const numberValue = Number(value);

  return Number.isNaN(numberValue) ? null : numberValue;
};

const getFileId = (fileItem) => {
  if (!fileItem) return null;

  return (
    fileItem.formFileId ||
    fileItem.collegeFormFileId ||
    fileItem.fileId ||
    fileItem.id ||
    null
  );
};

const getFileName = (fileItem) => {
  if (!fileItem) return "";

  return (
    fileItem.fileName ||
    fileItem.originalFileName ||
    fileItem.name ||
    "첨부파일"
  );
};

const normalizeFileList = (data) => {
  if (!data) return [];

  const fileList = [];

  const possibleArrays = [
    data.files,
    data.fileList,
    data.formFiles,
    data.formFileList,
    data.fileResponses,
    data.formFileResponses,
    data.attachments,
  ];

  possibleArrays.forEach((array) => {
    if (Array.isArray(array)) {
      fileList.push(...array);
    }
  });

  const possibleSingleFiles = [
    data.file,
    data.formFile,
    data.fileResponse,
    data.formFileResponse,
    data.attachment,
  ];

  possibleSingleFiles.forEach((fileItem) => {
    if (fileItem && typeof fileItem === "object") {
      fileList.push(fileItem);
    }
  });

  return fileList;
};

const getExistingFileIds = (fileList) => {
  if (!Array.isArray(fileList)) return [];

  const ids = fileList
    .map((fileItem) => getFileId(fileItem))
    .filter(Boolean);

  return [...new Set(ids)];
};

export default function OrgBoardForm({ mode = "write" }) {
  const navigate = useNavigate();
  const { unit, id } = useParams();

  const fileInputRef = useRef(null);

  const adminType = localStorage.getItem("adminType");
  const storedCollegeId = getStoredCollegeId();

  const selectedUnit = useMemo(() => {
    return unitList.find((item) => item.key === unit) || unitList[0];
  }, [unit]);

  const collegeId = useMemo(() => {
    if (adminType === "cc") {
      return storedCollegeId || selectedUnit.collegeId;
    }

    return selectedUnit.collegeId;
  }, [adminType, storedCollegeId, selectedUnit]);

  const isEdit = mode === "edit";

  const [title, setTitle] = useState("");
  const [listCategory, setListCategory] = useState("BUDGET");
  const [contents, setContents] = useState("");

  const [files, setFiles] = useState([]);
  const [existingFiles, setExistingFiles] = useState([]);
  const [keepFileIds, setKeepFileIds] = useState([]);

  const [loading, setLoading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  useEffect(() => {
    if (!isEdit || !id || !collegeId) return;

    const fetchDetail = async () => {
      try {
        setLoading(true);

        const result = await apiClient(`/api/colleges/${collegeId}/form/${id}`);
        const data = result?.data || result;

        if (!data) {
          alert("게시글을 찾을 수 없습니다.");
          navigate("..", { replace: true });
          return;
        }

        const detailFiles = normalizeFileList(data);
        const detailKeepFileIds = getExistingFileIds(detailFiles);

        setTitle(data.title || "");
        setListCategory(data.listCategory || data.category || "BUDGET");
        setContents(data.content || data.contents || "");
        setExistingFiles(detailFiles);
        setKeepFileIds(detailKeepFileIds);
        setFiles([]);

        console.log("중운위 게시글 상세 조회:", data);
        console.log("기존 파일 목록:", detailFiles);
        console.log("기존 파일 유지 ID:", detailKeepFileIds);
      } catch (error) {
        console.error("조직별 게시글 상세 조회 오류:", error);
        alert(error.message || "게시글을 불러오지 못했습니다.");
      } finally {
        setLoading(false);
      }
    };

    fetchDetail();
  }, [isEdit, id, collegeId, navigate]);

  const openFilePicker = () => {
    if (loading) return;

    fileInputRef.current?.click();
  };

  const applySelectedFiles = (fileList) => {
    const selectedFiles = Array.from(fileList || []);

    if (selectedFiles.length === 0) return;

    setFiles((prevFiles) => {
      const mergedFiles = [...prevFiles];

      selectedFiles.forEach((selectedFile) => {
        const isDuplicate = mergedFiles.some(
          (file) =>
            file.name === selectedFile.name &&
            file.size === selectedFile.size &&
            file.lastModified === selectedFile.lastModified
        );

        if (!isDuplicate) {
          mergedFiles.push(selectedFile);
        }
      });

      return mergedFiles;
    });
  };

  const handleFileChange = (e) => {
    applySelectedFiles(e.target.files);

    e.target.value = "";
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (loading) return;

    setIsDragOver(true);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (loading) return;

    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.currentTarget.contains(e.relatedTarget)) return;

    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    setIsDragOver(false);

    if (loading) return;

    applySelectedFiles(e.dataTransfer.files);
  };

  const handleRemoveNewFile = (targetIndex, e) => {
    e.stopPropagation();

    setFiles((prevFiles) =>
      prevFiles.filter((_, fileIndex) => fileIndex !== targetIndex)
    );
  };

  const handleRemoveExistingFile = (targetFile, e) => {
    e.stopPropagation();

    const targetFileId = getFileId(targetFile);

    setExistingFiles((prevFiles) =>
      prevFiles.filter((fileItem) => getFileId(fileItem) !== targetFileId)
    );

    if (targetFileId) {
      setKeepFileIds((prevIds) =>
        prevIds.filter((fileId) => String(fileId) !== String(targetFileId))
      );
    }
  };

  const handleCancel = () => {
    navigate("..");
  };

  const handleSave = async () => {
    if (!collegeId) {
      alert(
        "소속 단과대 정보를 확인할 수 없습니다. 다시 로그인한 뒤 시도해 주세요."
      );
      return;
    }

    if (!title.trim()) {
      alert("제목을 입력해 주세요.");
      return;
    }

    if (!contents.trim()) {
      alert("글 내용을 입력해 주세요.");
      return;
    }

    const formData = new FormData();

    formData.append("title", title);
    formData.append("listCategory", listCategory);
    formData.append("contents", contents);

    if (isEdit) {
      keepFileIds.forEach((fileId) => {
        formData.append("keepFileIds", fileId);
      });
    }

    files.forEach((file) => {
      formData.append("files", file);
    });

    console.log("중운위 게시글 저장 files:", files);
    console.log("중운위 게시글 저장 keepFileIds:", keepFileIds);

    for (const pair of formData.entries()) {
      console.log("OrgBoard FormData:", pair[0], pair[1]);
    }

    try {
      setLoading(true);

      if (isEdit) {
        await apiClient(`/api/colleges/${collegeId}/form/${id}`, {
          method: "PUT",
          body: formData,
        });

        alert("수정되었습니다.");
      } else {
        await apiClient(`/api/colleges/${collegeId}/form`, {
          method: "POST",
          body: formData,
        });

        alert("등록되었습니다.");
      }

      navigate("..");
    } catch (error) {
      console.error("조직별 게시글 저장 오류:", error);
      alert(error.message || "저장 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  const hasAnyFile = existingFiles.length > 0 || files.length > 0;

  return (
    <div className="org-board-form-page admin-scroll-x">
      <div className="org-board-form-inner">
        <h2 className="org-board-form-title">
          {isEdit ? "게시글 수정" : "새 게시글 작성"}
        </h2>

        <div className="org-board-form-row">
          <Label>제목</Label>

          <input
            className="org-board-form-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="제목을 입력해 주세요."
            disabled={loading}
          />
        </div>

        <div className="org-board-form-row">
          <Label>카테고리</Label>

          <div className="org-board-category-list">
            {categoryOptions.map((item) => (
              <button
                key={item.value}
                type="button"
                className={
                  listCategory === item.value
                    ? "org-board-category active"
                    : "org-board-category"
                }
                onClick={() => setListCategory(item.value)}
                disabled={loading}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        <div className="org-board-form-row">
          <Label>파일 첨부</Label>

          <div className="org-board-file-area">
            <input
              ref={fileInputRef}
              className="org-board-file-hidden-input"
              type="file"
              multiple
              onChange={handleFileChange}
              disabled={loading}
            />

            <button
              type="button"
              className="org-board-file-btn"
              onClick={openFilePicker}
              disabled={loading}
            >
              내 PC
            </button>

            <div
              className={`org-board-file-drop ${
                isDragOver ? "is-drag-over" : ""
              }`}
              role="button"
              tabIndex={0}
              onClick={openFilePicker}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  openFilePicker();
                }
              }}
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {hasAnyFile ? (
                <>
                  {existingFiles.map((file, index) => (
                    <div
                      className="org-board-file-item"
                      key={getFileId(file) || `${getFileName(file)}-${index}`}
                    >
                      <button
                        type="button"
                        className="org-board-file-remove"
                        onClick={(e) => handleRemoveExistingFile(file, e)}
                        disabled={loading}
                      >
                        ×
                      </button>

                      <span>{getFileName(file)}</span>
                    </div>
                  ))}

                  {files.map((file, index) => (
                    <div
                      className="org-board-file-item"
                      key={`${file.name}-${file.size}-${file.lastModified}`}
                    >
                      <button
                        type="button"
                        className="org-board-file-remove"
                        onClick={(e) => handleRemoveNewFile(index, e)}
                        disabled={loading}
                      >
                        ×
                      </button>

                      <span>{file.name}</span>
                    </div>
                  ))}
                </>
              ) : (
                <span>파일을 클릭하거나 드래그해서 첨부하세요.</span>
              )}
            </div>
          </div>
        </div>

        <div className="org-board-form-row vertical">
          <Label>글쓰기</Label>

          <textarea
            className="org-board-form-textarea"
            value={contents}
            onChange={(e) => setContents(e.target.value)}
            placeholder="글을 작성해 주세요.(최대 5,000자까지 입력 가능합니다.)"
            disabled={loading}
          />
        </div>

        <div className="org-board-form-footer">
          <button
            type="button"
            className="org-board-cancel-btn"
            onClick={handleCancel}
            disabled={loading}
          >
            취소
          </button>

          <button
            type="button"
            className="org-board-save-btn"
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? "저장 중..." : "저장"}
          </button>
        </div>
      </div>
    </div>
  );
}